/**
 * SnapshotResults Page
 * Displays snapshot results with Primary Fault Card, Strength Pills, Result Ranking Rows
 * CTA Button to "Build Training Plan"
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, ChevronRight } from 'lucide-react';
import { PrimaryFaultCard } from '@/components/snapshot/PrimaryFaultCard';
import { StrengthPills } from '@/components/snapshot/StrengthPills';
import { ResultRankingRow } from '@/components/snapshot/ResultRankingRow';
import { RootCauseChainDisplay } from '@/components/snapshot/RootCauseChainDisplay';
import { GolfButton } from '@/components/golf/golf-button';
import { GolfCard, GolfCardContent, GolfCardHeader, GolfCardTitle } from '@/components/golf/golf-card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { getSnapshot, getSnapshotV2, type SnapshotResponse, type SnapshotV2Response } from '@/lib/api/snapshot';
import { BottomTabs } from '@/mvp/navigation/BottomTabs';
import { ScoreInfoIcon } from '@/components/scoring/ScoreInfoIcon';
import { useActivePlan } from '@/hooks/useActivePlan';
import { generateTrainingProgram, type TrainingPlanIntake } from '@/lib/api/training-program';
import { TrainingPlanIntakeModal, TrainingPlanReplaceConfirmModal } from '@/components/training-program/TrainingPlanIntakeModal';

export default function SnapshotResults() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState<SnapshotResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [showIntake, setShowIntake] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submittingPlan, setSubmittingPlan] = useState(false);
  const { hasActivePlan, loading: planLoading } = useActivePlan();
  const [snapshotV2, setSnapshotV2] = useState<SnapshotV2Response | null>(null);
  const [snapshotV2Loading, setSnapshotV2Loading] = useState(true);

  const loadSnapshotV2 = async (runId: string) => {
    try {
      setSnapshotV2Loading(true);
      const v2Data = await getSnapshotV2(runId);
      setSnapshotV2(v2Data);
    } catch (error) {
      console.warn('Snapshot V2 not available:', error);
      setSnapshotV2(null);
    } finally {
      setSnapshotV2Loading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadSnapshotV2(id);  // Try V2 first
      loadSnapshot(id);    // Also try legacy for backwards compatibility
    }
  }, [id]);

  const loadSnapshot = async (snapshotId: string) => {
    try {
      setLoading(true);
      const data = await getSnapshot(snapshotId);
      setSnapshot(data);
    } catch (error: any) {
      // Don't show error for legacy 404 - V2 might still work
      console.log('Legacy snapshot not found:', snapshotId);
    } finally {
      setLoading(false);
    }
  };

  const handleBuildTrainingPlan = () => {
    if (planLoading) return;
    if (hasActivePlan) {
      setShowConfirm(true);
      return;
    }
    setShowIntake(true);
  };

  const handleConfirmReplace = () => {
    setShowConfirm(false);
    setShowIntake(true);
  };

  const handleSubmitIntake = async (intake: TrainingPlanIntake) => {
    if (!snapshotV2) {
      toast.error('Snapshot V2 required to create training plan');
      return;
    }
    try {
      setSubmittingPlan(true);
      await generateTrainingProgram({
        snapshot_v2_run_id: snapshotV2.run_id,
        intake,
        action: hasActivePlan ? 'replace' : 'new',
      });
      setShowIntake(false);
      toast.success('Training plan created');
      navigate('/training-program');
    } catch (error: any) {
      console.error('Failed to create training plan:', error);
      toast.error(error.message || 'Failed to create training plan');
    } finally {
      setSubmittingPlan(false);
    }
  };

  // Wait for both loading states
  if (loading || snapshotV2Loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Need at least one source of data
  if (!snapshot && !snapshotV2) {
    return (
      <div className="min-h-screen bg-background p-6">
        <GolfCard>
          <GolfCardContent className="text-center py-12">
            <p className="text-lg font-semibold text-foreground mb-2">
              Snapshot not found
            </p>
            <GolfButton onClick={() => navigate('/snapshot/select')} className="mt-4">
              Create New Snapshot
            </GolfButton>
          </GolfCardContent>
        </GolfCard>
      </div>
    );
  }

  // Get primary fault info from V2 or legacy
  const primaryFaultId = snapshotV2?.primary_fault 
    || snapshot?.faults?.find(f => f.taxonomy_key === snapshot?.snapshot?.primary_fault)?.taxonomy_key
    || snapshot?.faults?.[0]?.taxonomy_key;
  
  const primaryFaultName = primaryFaultId
    ? primaryFaultId.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
    : 'Primary Fault';
    
  // Get consistent patterns from V2 for display
  const consistentPatterns = snapshotV2?.consistent_patterns || [];

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">
            Snapshot Results
          </h1>
          <p className="text-sm text-muted-foreground">
            Analysis aggregated from 5 swings
          </p>
          {snapshotV2 && (
            <p className="text-sm text-muted-foreground mt-1">
              This training plan will be built from the 5 swings you selected.
            </p>
          )}
        </div>

        {/* Primary Fault Card - V2 or legacy */}
        {primaryFaultId && (
          <PrimaryFaultCard
            taxonomyKey={primaryFaultId}
            displayName={primaryFaultName}
            avgSeverity={consistentPatterns[0]?.severity_range?.average ?? snapshot?.faults?.[0]?.avg_severity ?? 2}
            consistency={consistentPatterns[0]?.consistency_score ?? snapshot?.faults?.[0]?.consistency ?? 0.8}
          />
        )}

        {/* Root Cause Chain - Why this fault? */}
        {snapshotV2?.root_cause_chain && snapshotV2.root_cause_chain.length > 0 && (
          <Collapsible className="w-full">
            <GolfCard>
              <CollapsibleTrigger className="w-full">
                <GolfCardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <div className="flex items-center justify-between w-full">
                    <GolfCardTitle className="text-base">Why this fault?</GolfCardTitle>
                    <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-90" />
                  </div>
                </GolfCardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <GolfCardContent>
                  <RootCauseChainDisplay chain={snapshotV2.root_cause_chain} />
                </GolfCardContent>
              </CollapsibleContent>
            </GolfCard>
          </Collapsible>
        )}

        {/* Strengths - only if legacy snapshot has them */}
        {snapshot?.strengths && snapshot.strengths.length > 0 && (
          <GolfCard>
            <GolfCardContent>
              <StrengthPills strengths={snapshot.strengths} />
            </GolfCardContent>
          </GolfCard>
        )}

        {/* Consistent Patterns from V2 */}
        {snapshotV2 && consistentPatterns.length > 1 && (
          <GolfCard>
            <GolfCardHeader>
              <GolfCardTitle>Consistent Patterns</GolfCardTitle>
            </GolfCardHeader>
            <GolfCardContent>
              <div className="space-y-2">
                {consistentPatterns.slice(1).map((pattern, index) => (
                  <div key={pattern.flaw_id || index} className="flex items-center justify-between py-2 border-b last:border-0">
                    <span className="text-sm font-medium">
                      {pattern.flaw_id?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {pattern.frequency}/5 swings
                    </span>
                  </div>
                ))}
              </div>
            </GolfCardContent>
          </GolfCard>
        )}

        {/* Swing Quality Summary - Only for legacy, V2 doesn't score */}
        {!snapshotV2 && snapshot && (
          <GolfCard>
            <GolfCardHeader>
              <div className="flex items-center justify-between">
                <GolfCardTitle>Overall Swing Quality</GolfCardTitle>
                <ScoreInfoIcon 
                  score={snapshot.snapshot.swing_quality} 
                  snapshotId={id}
                />
              </div>
            </GolfCardHeader>
            <GolfCardContent>
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <div className="text-4xl font-bold text-primary">
                    {snapshot.snapshot.swing_quality.toFixed(0)}
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Snapshot quality from top 3 faults
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Peer label: {snapshot.snapshot.peer_label}
                </p>
                <GolfButton
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => navigate(`/score-explanation/snapshot/${id}`)}
                >
                  View How Score Was Calculated
                </GolfButton>
              </div>
            </GolfCardContent>
          </GolfCard>
        )}

        {/* Result Ranking Rows - only if legacy snapshot has swing runs */}
        {snapshot?.swing_runs && snapshot.swing_runs.length > 0 && (
          <GolfCard>
            <GolfCardHeader>
              <GolfCardTitle>Swing Rankings</GolfCardTitle>
            </GolfCardHeader>
            <GolfCardContent className="space-y-2">
              {snapshot.swing_runs.map((swing, index) => (
                <ResultRankingRow
                  key={swing.id}
                  swing={swing}
                  rank={index + 1}
                />
              ))}
            </GolfCardContent>
          </GolfCard>
        )}

        {/* CTA Button */}
        <div className="sticky bottom-0 left-0 right-0 z-50 bg-background border-t border-border p-4 pb-safe">
          <GolfButton
            onClick={handleBuildTrainingPlan}
            className="w-full"
            size="lg"
            disabled={planLoading || snapshotV2Loading || !snapshotV2}
          >
            {snapshotV2Loading
              ? 'Loading...'
              : !snapshotV2
                ? 'Snapshot V2 Required'
                : hasActivePlan
                  ? 'Update Training Plan Using This Analysis'
                  : 'Create Training Plan from This Analysis'}
          </GolfButton>
          {!snapshotV2Loading && !snapshotV2 && (
            <p className="text-sm text-amber-600 text-center mt-2">
              Training plans require Snapshot V2. Please create a new snapshot with 5 analyzed swings.
            </p>
          )}
        </div>
      </div>
      
      {/* Bottom Navigation */}
      <BottomTabs />

      <TrainingPlanReplaceConfirmModal
        isOpen={showConfirm}
        onCancel={() => setShowConfirm(false)}
        onContinue={handleConfirmReplace}
      />

      <TrainingPlanIntakeModal
        isOpen={showIntake}
        onClose={() => setShowIntake(false)}
        primaryFaultName={primaryFaultName}
        onSubmit={handleSubmitIntake}
        isSubmitting={submittingPlan}
      />
    </div>
  );
}


